// Service page content for Partner Visa (820/801 onshore, 309/100 offshore).
// Sourced from docs/research/australian-visa-pathways.md §3.
// COMPLIANCE: we gather and organise relationship evidence; a registered
// migration agent gives the migration advice, confirms the subclass, and lodges.
// No guaranteed outcomes; honest about cost and processing without quoting
// unverified figures as fact; empathetic tone.

const partnerVisa = {
  groupSlug: "family-partner",
  slug: "partner-visa",
  title: "Partner Visa (820/801, 309/100)",
  eyebrow: "Family & Partner",
  intro:
    "A partner visa lets the spouse or de facto partner of an Australian citizen, permanent resident, or eligible New Zealand citizen live in Australia and progress to permanent residence. It is granted in two stages, and the heart of every application is genuine, well-evidenced proof of your relationship.",

  chips: [
    { label: "Visa type", value: "Two stage, leads to PR" },
    { label: "Sponsor", value: "Citizen, PR, or eligible NZ citizen" },
    { label: "Onshore", value: "820 then 801" },
    { label: "Offshore", value: "309 then 100" },
  ],

  whoFor: [
    "Married or de facto partners of an Australian citizen, permanent resident, or eligible New Zealand citizen.",
    "Couples applying from inside Australia (820/801) or from overseas (309/100).",
    "People who want their relationship presented thoroughly and honestly.",
  ],

  essentials: [
    {
      title: "A genuine, continuing relationship",
      detail:
        "You must show a genuine and continuing married or de facto relationship. De facto partners generally need to show 12 months together, with some exceptions such as a registered relationship.",
    },
    {
      title: "An eligible sponsor",
      detail:
        "Your partner must be an Australian citizen, permanent resident, or eligible New Zealand citizen, and must meet the sponsorship and character requirements.",
    },
    {
      title: "Strong relationship evidence",
      detail:
        "Financial, household, social, and commitment evidence is the core of the application. Building this well is where cases are won or lost.",
    },
    {
      title: "Onshore or offshore lodgement",
      detail:
        "Apply onshore for the 820 then 801, or offshore for the 309 then 100. Onshore applicants usually get a bridging visa to remain lawfully while it is processed.",
    },
    {
      title: "Two stages",
      detail:
        "The temporary visa is granted first, and the permanent visa is assessed around two years after lodgement.",
    },
    {
      title: "Health and character",
      detail:
        "You and any children included must meet the Department's health and character requirements.",
    },
  ],

  process: [
    {
      title: "Match with a registered agent",
      detail:
        "A registered migration agent confirms eligibility, the right subclass, and whether to apply onshore or offshore.",
    },
    {
      title: "Build the evidence",
      detail:
        "We help you gather and organise relationship evidence into a clear, persuasive picture.",
    },
    {
      title: "Prepare statements",
      detail:
        "We prepare and organise your relationship statements and sponsor documents so nothing important is missed.",
    },
    {
      title: "Lodge the application",
      detail:
        "A registered migration agent lodges the visa and, for onshore applicants, deals with the bridging visa so you remain lawfully in Australia.",
    },
    {
      title: "Manage the wait",
      detail:
        "We keep your file current and coordinated through processing, and a registered migration agent responds to any requests from the Department.",
    },
    {
      title: "Permanent stage",
      detail:
        "At around two years, we organise your permanent (801 or 100) stage documents so a registered migration agent can progress you to permanent residence.",
    },
  ],

  faqs: [
    {
      q: "How long does a partner visa take?",
      a: "Processing times are set by the Department and have been long in recent periods, often well over a year for the first stage. A registered migration agent gives you a realistic, current timeframe for your situation rather than a best-case promise.",
    },
    {
      q: "What does it cost?",
      a: "The partner visa has one of the highest Visa Application Charges in the system, paid once to cover both stages. The exact amount is set by the government and changes, so a registered migration agent confirms the current figure before you budget.",
    },
    {
      q: "Can I stay in Australia while it is processed?",
      a: "If you apply onshore for the 820, you are generally granted a bridging visa that lets you remain in Australia lawfully while the application is processed. A registered migration agent handles this as part of lodging.",
    },
    {
      q: "What if we have not lived together for 12 months?",
      a: "There are exceptions, including registering your relationship in some states, or applying as a married couple. A registered migration agent looks at your circumstances and advises the strongest approach, and we gather and organise the evidence to support it.",
    },
    {
      q: "How important is the relationship evidence?",
      a: "It is everything. The Department decides based on whether your relationship is genuine and continuing, evidenced across finances, household, social life, and commitment. Gathering and organising this properly is the single biggest thing we do for you.",
    },
  ],

  related: ["prospective-marriage-300", "child-visa", "parent-visa"],

  lastVerified: "13 July 2026",
};

export default partnerVisa;
